import React, { useEffect, useState } from "react";
import { Outlet, useParams, useNavigate } from "react-router-dom";
import { Box, CircularProgress, Typography, Button } from "@mui/material";

import { HttpOrder } from "../http";
import { OrderCheckoutResumeDTO } from "../types/purchaseOrder/OrderResume";
import { useCartStore, type CartItem } from "@ecommerce/cart";

import { ShippingProvider, CheckoutResumeProvider } from "@ecommerce/checkout";

import { configurationEncoder } from "@ecommerce/shared-utils";
import { OrderResumeToken } from "../utils/orderResumeToken";

const mapResumeProductsToCartItems = (resume: OrderCheckoutResumeDTO): CartItem[] => {
    return (resume.Productos || []).map((p) => ({
        idProduct: p.idProducto,
        idProductVariant: p.idProductoVariante,
        name: p.nombre,
        price: p.precio,
        quantity: p.cantidad,
        image: p.urlImagenPrincipal,
        stock: undefined,
        attributes: (p.atributos || []).map((a) => ({
            name: a.nombreAtributo,
            value: a.nombreAtributoValor,
        })),
    }));
};

const CheckoutResumeRoute: React.FC = () => {
    const { token } = useParams<{ token: string }>();
    const navigate = useNavigate();
    const beginCartOverride = useCartStore((s) => s.beginCartOverride);
    const endCartOverride = useCartStore((s) => s.endCartOverride);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [resume, setResume] = useState<OrderCheckoutResumeDTO | null>(null);
    const [hydrated, setHydrated] = useState(false);

    const [orderGuid, setOrderGuid] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        let overrideStarted = false;

        const run = async () => {
            if (!token) {
                setError("No se encontró el identificador para reanudar la orden.");
                setLoading(false);
                return;
            }

            setLoading(true);
            setError(null);
            setResume(null);
            setHydrated(false);
            setOrderGuid(null);

            try {
                const encoder = await configurationEncoder.getConfiguration();

                let guid: string;
                try {
                    guid = OrderResumeToken.decodeGuid(encoder, token);
                } catch {
                    throw new Error(
                        "El link de reanudación no es válido (no se pudo decodificar).",
                    );
                }

                if (!OrderResumeToken.isGuid(guid)) {
                    throw new Error("El link de reanudación no es válido (formato incorrecto).");
                }

                const data = await HttpOrder.getCheckoutResume(guid);
                if (cancelled) return;

                setOrderGuid(guid);
                setResume(data);

                const items = mapResumeProductsToCartItems(data);
                beginCartOverride(items);
                overrideStarted = true;
                setHydrated(true);
            } catch (e: any) {
                if (cancelled) return;
                setError(e?.message || "No se pudo recuperar la orden para reanudar.");
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        run();

        // Al desmontar: volver al carrito persistido más reciente.
        return () => {
            cancelled = true;
            if (overrideStarted) endCartOverride();
        };
    }, [token, beginCartOverride, endCartOverride]);

    if (loading) {
        return (
            <Box
                sx={{
                    minHeight: "60vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                    gap: 2,
                }}
            >
                <CircularProgress />
                <Typography>Recuperando tu orden para continuar el checkout…</Typography>
            </Box>
        );
    }

    if (error || !resume || !hydrated || !orderGuid || !token) {
        return (
            <Box
                sx={{
                    minHeight: "60vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                    gap: 2,
                    px: 2,
                }}
            >
                <Typography variant="h6">No se pudo reanudar la compra</Typography>
                <Typography color="text.secondary" sx={{ textAlign: "center" }}>
                    {error || "La orden no está disponible."}
                </Typography>
                <Button variant="contained" onClick={() => navigate("/")}>
                    Volver a la tienda
                </Button>
            </Box>
        );
    }

    return (
        <ShippingProvider>
            <CheckoutResumeProvider resume={resume} orderGuid={orderGuid} resumeToken={token}>
                <Outlet />
            </CheckoutResumeProvider>
        </ShippingProvider>
    );
};

export default CheckoutResumeRoute;
